import type { GameLike } from './types'

/**
 * `game_status_id`, as the API defines it (`App\Models\GameStatus`).
 *
 * Mirrored here rather than imported from `common/constants/TypedGameStatus` so
 * this module keeps one dependency — its own types file — and can be read
 * against the API contract alone.
 */
export const GAME_STATUS_ID = {
  NORMAL: 1,
  HOME_FORFEIT: 2,
  VISITING_FORFEIT: 3,
  DELAYED: 4,
  POSTPONED: 5,
  SUSPENDED: 6,
  DOUBLE_FORFEIT: 7,
  PRE_GAME: 8,
  FINAL: 9,
} as const

/**
 * The state a reader cares about, independent of how it is spelled.
 *
 * `scheduled` is the absence of everything else, not a value the API sets: a
 * game that has not started, has not been called off and carries no result.
 */
export type GameStatusKey =
  | 'scheduled'
  | 'inProgress'
  | 'final'
  | 'delayed'
  | 'postponed'
  | 'suspended'
  | 'cancelled'

/** How a status should read at a glance. Drives colour, nothing else. */
export type GameStatusTone = 'neutral' | 'live' | 'done' | 'warning' | 'danger'

/** The forfeit a result was decided by, when it was decided by one. */
export type GameForfeit = 'home' | 'visiting' | 'double'

export interface GameStatusInfo {
  key: GameStatusKey
  tone: GameStatusTone
  /**
   * Set when the result came from a forfeit. It qualifies `final` rather than
   * replacing it — "Final - Home Forfeit" is one status, not two.
   */
  forfeit: GameForfeit | null
  /**
   * Stats were rolled back: the score on screen is provisional and the game is
   * being re-entered. The List view has always said so, and a Status column
   * that stayed silent would be the more confident of the two and the wrong one.
   */
  inReview: boolean
  /**
   * Sort order for a Status column: what has not happened, then what is
   * happening, then what finished, then what went wrong. Alphabetical would put
   * Cancelled above Final above In Progress, which is noise.
   */
  rank: number
}

const RANK: Record<GameStatusKey, number> = {
  scheduled: 0,
  inProgress: 1,
  final: 2,
  delayed: 3,
  postponed: 4,
  suspended: 5,
  cancelled: 6,
}

const TONE: Record<GameStatusKey, GameStatusTone> = {
  scheduled: 'neutral',
  inProgress: 'live',
  final: 'done',
  delayed: 'warning',
  postponed: 'warning',
  suspended: 'warning',
  cancelled: 'danger',
}

const FORFEIT_BY_STATUS_ID: Record<number, GameForfeit> = {
  [GAME_STATUS_ID.HOME_FORFEIT]: 'home',
  [GAME_STATUS_ID.VISITING_FORFEIT]: 'visiting',
  [GAME_STATUS_ID.DOUBLE_FORFEIT]: 'double',
}

const isSet = (value: string | null | undefined): boolean =>
  value != null && value !== ''

/**
 * What state a game is in, from the fields the games LIST returns.
 *
 * The list resource is the constraint. It always carries `game_status_id`,
 * `started_at` and `ended_at`; it adds scores and `current_period` only once a
 * game has started or been forfeited, and `cancelled_at` only when the game is
 * cancelled. So this reads exactly those, and never a field a row might not
 * have.
 *
 * The order of the checks is the whole logic:
 *
 * 1. **Cancelled** outranks everything — the game is not going to be played,
 *    whatever else is set on it. (These views request
 *    `exclude_cancelled_games=1`, so it is rare rather than unreachable: a game
 *    cancelled while the sheet is open is still in the loaded page.)
 * 2. **A forfeit is a result**, so it reads as Final with the forfeit named,
 *    with or without `ended_at`. A forfeited game is often never started, and
 *    calling it "Scheduled" is the exact confusion this exists to remove.
 * 3. **Ended** is Final.
 * 4. **Postponed / Suspended / Delayed** describe a game still on the books, so
 *    they only apply while there is no result to report.
 * 5. **Started** is In Progress, and everything left is Scheduled.
 */
export const deriveGameStatus = (game: GameLike): GameStatusInfo => {
  const statusId =
    game.game_status_id == null ? null : Number(game.game_status_id)
  const forfeit = statusId === null ? null : FORFEIT_BY_STATUS_ID[statusId]
  const inReview = game.stats_rolledback === true

  const info = (
    key: GameStatusKey,
    withForfeit: GameForfeit | null = null
  ) => ({
    key,
    tone: TONE[key],
    forfeit: withForfeit,
    inReview,
    rank: RANK[key],
  })

  if (isSet(game.cancelled_at)) {
    return info('cancelled')
  }

  if (forfeit !== undefined && forfeit !== null) {
    return info('final', forfeit)
  }

  if (isSet(game.ended_at)) {
    return info('final')
  }

  if (statusId === GAME_STATUS_ID.POSTPONED) {
    return info('postponed')
  }
  if (statusId === GAME_STATUS_ID.SUSPENDED) {
    return info('suspended')
  }
  if (statusId === GAME_STATUS_ID.DELAYED) {
    return info('delayed')
  }

  if (isSet(game.started_at)) {
    return info('inProgress')
  }

  return info('scheduled')
}

/**
 * True when a score means something.
 *
 * A blank Score cell is ambiguous on its own — "not played yet" and "played,
 * nobody entered it" look identical — which is half of what the Status column
 * is for. This is the other half: a scheduled game is not MISSING its score.
 */
export const hasMeaningfulScore = (status: GameStatusInfo): boolean =>
  status.key === 'final' || status.key === 'inProgress'
